import { useEffect, useState } from 'react';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AiOutlinePlus } from 'react-icons/ai';
import { IoMdClose } from 'react-icons/io';

import { ConfirmationModal } from '../FeedBack/ConfirmationModal';
import ButtonHeadless from '../General/ButtonHeadless';
import { Language, Languages } from '@wajeez-web/core/utils';

interface LanguageTabsProps {
  languages?: Language[];
  defaultSelectedLanguageValue?: Languages;
  selectedLanguageValue: Languages;
  onSelect: (value: Languages) => void;
  addedLanguages?: Languages[];
  onAddedLanguagesChange: (languages: Languages[], language: Languages) => void;
}

export const LanguagesTabs = ({
  languages = [
    {
      value: Languages.AR,
      label: 'Arabic',
      activeColor: '#1f798320',
      inactiveColor: '#1f7983',
    },
    {
      value: Languages.EN,
      label: 'English',
      activeColor: '#64008020',
      inactiveColor: '#640080',
    },
  ],
  defaultSelectedLanguageValue = Languages.AR,
  selectedLanguageValue,
  addedLanguages = [Languages.AR],
  onSelect,
  onAddedLanguagesChange,
}: LanguageTabsProps) => {
  const [selectedLanguageValueLocal, setSelectedLanguageValueLocal] =
    useState<Languages>(
      selectedLanguageValue === undefined
        ? selectedLanguageValue
        : defaultSelectedLanguageValue
    );
  const [addedLanguagesLocal, setAddedLanguagesLocal] =
    useState(addedLanguages);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const isEnglishAdded =
    addedLanguagesLocal.find((l) => l === Languages.EN) !== undefined;
  const isArabicAdded =
    addedLanguagesLocal.find((l) => l === Languages.AR) !== undefined;
  const isLanguageRemoveIconShown = addedLanguagesLocal.length > 1;

  const dir = useCommonSelector(({ common }) => common.settings.dir);

  const removeLanguage = (value: Languages) => {
    setAddedLanguagesLocal(addedLanguagesLocal.filter((l) => l !== value));
  };

  useEffect(() => {
    setAddedLanguagesLocal(addedLanguages);
  }, [addedLanguages.length]);

  const renderAddedLanguages = () => {
    return addedLanguagesLocal.map((l) => {
      const language = languages.find((ll) => ll.value === l);

      const isSelected = selectedLanguageValueLocal === l;

      return (
        <div key={l}>
          <div
            className={`py-3 px-6 flex justify-center items-center ${
              isSelected ? 'text-black' : 'text-white'
            } font-bold cursor-pointer`}
            style={{
              backgroundColor: isSelected
                ? language?.activeColor
                : language?.inactiveColor,
            }}
            onClick={() => {
              if (l !== selectedLanguageValueLocal) {
                onSelect(l);

                setSelectedLanguageValueLocal(l);
              }
            }}
          >
            {isLanguageRemoveIconShown && dir === 'ltr' && (
              <div
                className="ml-2 cursor-pointer"
                onClick={() => {
                  setIsConfirmationModalOpen(true);
                }}
              >
                <IoMdClose />
              </div>
            )}
            <ReturnTranslatedText
              text={`inputs.labels.${language?.label}`}
              origintext={language?.label}
            />
            {isLanguageRemoveIconShown && dir === 'rtl' && (
              <div
                className="mr-2 cursor-pointer"
                onClick={() => {
                  setIsConfirmationModalOpen(true);
                }}
              >
                <IoMdClose />
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="w-full flex justify-start items-center">
      <div className="flex justify-start items-center overflow-hidden">
        {renderAddedLanguages()}
      </div>
      {!isEnglishAdded && (
        <AddLanguageButton
          onClick={() => {
            setAddedLanguagesLocal([...addedLanguagesLocal, Languages.EN]);

            setSelectedLanguageValueLocal(Languages.EN);

            onAddedLanguagesChange(
              [...addedLanguagesLocal, Languages.EN],
              Languages.EN
            );

            onSelect(Languages.EN);
          }}
          label="English"
        />
      )}
      {!isArabicAdded && (
        <AddLanguageButton
          onClick={() => {
            setAddedLanguagesLocal([...addedLanguagesLocal, Languages.AR]);

            setSelectedLanguageValueLocal(Languages.AR);

            onAddedLanguagesChange(
              [...addedLanguagesLocal, Languages.AR],
              Languages.AR
            );

            onSelect(Languages.AR);
          }}
          label="Arabic"
        />
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => {
            setIsConfirmationModalOpen(false);
          }}
          message="The entered data will be lost, are you sure that you want to remove this language?"
          onSubmit={() => {
            removeLanguage(selectedLanguageValueLocal);

            const newSelectedLanguageValue =
              addedLanguagesLocal.find(
                (l) => l !== selectedLanguageValueLocal
              ) || Languages.AR;

            setSelectedLanguageValueLocal(newSelectedLanguageValue);

            onSelect(newSelectedLanguageValue);

            setIsConfirmationModalOpen(false);

            onAddedLanguagesChange(
              addedLanguages.filter((l) => l !== selectedLanguageValueLocal),
              selectedLanguageValueLocal
            );
          }}
        />
      )}
    </div>
  );
};

interface AddLanguageButtonProps {
  onClick: () => void;
  label: string;
}

export const AddLanguageButton = ({
  onClick,
  label,
}: AddLanguageButtonProps) => {
  const dir = useCommonSelector(({ common }) => common.settings.dir);

  return (
    <div className="ms-4 w-[130px]">
      <ButtonHeadless
        bgStyle="bg-transparent"
        paddingYStyle="py-2"
        borderRadiusStyle="rounded-none"
        action={onClick}
      >
        <div className="text-primary flex justify-center items-center">
          {dir === 'ltr' && (
            <div className="ml-2">
              <AiOutlinePlus />
            </div>
          )}
          <ReturnTranslatedText
            text={`buttons.${'Add ' + label}`}
            origintext={'Add ' + label}
          />
          {dir === 'rtl' && (
            <div className="mr-2">
              <AiOutlinePlus />
            </div>
          )}
        </div>
      </ButtonHeadless>
    </div>
  );
};
